import {ITractDataValue} from '../../API';
import {fillBuckets, opacityBuckets} from '../ColorUtils';
import {formatNumber, getAllValues, getAllValuesPositionsPercentile, getMinMax, getValueBucket} from '../MathUtils';
import {IDatasetConfig, IDatasetOptions, ITract2DBucketMap, ITractColorMap} from './Types';

const getValue = (_config: IDatasetConfig, row: ITractDataValue): number => {
  return +(row?.outageCI || 0);
};

export const ENOOutageCI: IDatasetOptions<number> = {
  id: 'ENOOutageCI',
  title: 'ENO Outage Customers Impacted',
  downloadTitle: 'ENO Outage Customers Impacted',
  combinable: true,
  cutoffDots: [6, 10],
  description: 'ENO Outage Customers Impacted shows the number of customers impacted by the outage.',
  isPositionPercentile: true,

  getValue,

  formatValue: (config: IDatasetConfig, value: number) => {
    return formatNumber(value);
  },

  getMinMax: (config: IDatasetConfig, rows: ITractDataValue[]) =>
    getMinMax(getAllValues(config, rows, getValue, ENOOutageCI.downloadTitle)),

  getValuesPercentile: (config: IDatasetConfig, rows: ITractDataValue[]) => {
    return getAllValuesPositionsPercentile(config, rows, getValue);
  },

  getPercentileValue: (config: IDatasetConfig, value: number, rows: ITractDataValue[]) => {
    const allValues = getAllValues(config, rows, getValue, ENOOutageCI.downloadTitle);
    const valuesArray = Object.values(allValues).sort((a, b) => a - b);
    const valueIndex = value ? Math.ceil((valuesArray.length - 1) * (value / 100)) : 0;

    return formatNumber(valuesArray[valueIndex]);
  },

  // This is a single-set color range. All cells are shades of orange
  getSingleSetColors: (config: IDatasetConfig, rows: ITractDataValue[]): ITractColorMap => {
    const valuesPositionsPercentile = ENOOutageCI.getValuesPercentile?.(config, rows) || {};

    const percentileMax = 100;

    const map: ITractColorMap = {fills: [], opacities: []};

    Object.entries(valuesPositionsPercentile).forEach(([tractId, value]) => {
      const bucket = getValueBucket(value, config.cutoff1, percentileMax, opacityBuckets.length);

      const color = fillBuckets[bucket];

      if (color) {
        map.fills.push(String(tractId));
        map.fills.push(color);

        map.opacities.push(String(tractId));
        map.opacities.push(1);
      }
    });

    return map;
  },

  // For 2D cases, we only ask the dataset to identify each value as being in one of three buckets, L, M, or H
  getDoubleSetBuckets: (config: IDatasetConfig, rows: ITractDataValue[]) => {
    const map = {values: {}} as ITract2DBucketMap;

    const valuesPositionsPercentile = ENOOutageCI.getValuesPercentile?.(config, rows) || {};
    // 10/12/2023 need to find out why this was done:
    // const values = getAllHouseCountsAsPercentages(config, rows, getValue, EnergyBurden.downloadTitle);
    const [min, max] = [0, 100] as [number, number];
    const floor = config.cutoff1 || min;
    const lCutoff = floor + (max - min) * 0.2;
    const hCutoff = floor + (max - min) * 0.8;

    Object.entries(valuesPositionsPercentile).forEach(([tractId, value]) => {
      if (value <= lCutoff) {
        map.values[tractId] = 'L';
      } else if (value >= hCutoff) {
        map.values[tractId] = 'H';
      } else {
        map.values[tractId] = 'M';
      }
    });

    return map;
  },
};
